import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, ChevronDown } from 'lucide-react';
import { communityService, Message } from '../../services/communityService';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';

const POLL_INTERVAL = 2000; // 2 seconds for near real-time feel
const ONLINE_STATUS_POLL_INTERVAL = 30000; // 30 seconds for online status

export default function DirectMessagesPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const storedUser = localStorage.getItem('user');
  const currentUserId = storedUser ? JSON.parse(storedUser).id : null;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const initialScrollDoneRef = useRef(false);

  const [messages, setMessages] = useState<Message[]>([]);
  const [otherUser, setOtherUser] = useState<any>(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [lastSeen, setLastSeen] = useState<string | null>(null);
  const onlineStatusPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const forceScrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (behavior === 'instant') {
      forceScrollToBottom();
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }
  }, [forceScrollToBottom]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollDown(distanceFromBottom > 200);
  };

  // Reset state when userId changes
  useEffect(() => {
    initialScrollDoneRef.current = false;
    setMessages([]);
    setPage(1);
    setLoading(true);
    lastMessageIdRef.current = null;
    setIsOnline(false);
    setLastSeen(null);
  }, [userId]);

  // Poll online status
  useEffect(() => {
    if (!userId) return;

    const fetchOnlineStatus = async () => {
      try {
        const status = await communityService.getUserOnlineStatus(userId);
        setIsOnline(status.isOnline);
        setLastSeen(status.lastSeen);
      } catch {
        // silently ignore
      }
    };

    fetchOnlineStatus();
    onlineStatusPollingRef.current = setInterval(fetchOnlineStatus, ONLINE_STATUS_POLL_INTERVAL);

    return () => {
      if (onlineStatusPollingRef.current) {
        clearInterval(onlineStatusPollingRef.current);
        onlineStatusPollingRef.current = null;
      }
    };
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadMessages();
      communityService.markMessagesAsRead(userId).then(() => {
        // Notify bell/badge to refresh unread count when entering conversation
        window.dispatchEvent(new CustomEvent('refresh-unread-messages'));
      }).catch(() => {});
    }
  }, [userId, page]);

  // Polling for new messages
  useEffect(() => {
    if (!userId || loading) return;

    const poll = async () => {
      if (isPollingRef.current) return;
      isPollingRef.current = true;
      try {
        const data = await communityService.getMessages(userId, 1);
        // Backend already returns messages in chronological order (oldest first)
        const incoming: Message[] = data.messages;
        if (incoming.length === 0) return;

        setMessages(prev => {
          const existingIds = new Set(prev.map((m: Message) => m.id));
          const newOnes = incoming.filter((m: Message) => !existingIds.has(m.id));
          if (newOnes.length === 0) return prev;
          communityService.markMessagesAsRead(userId).then(() => {
            // Notify bell/badge to refresh unread count instantly
            window.dispatchEvent(new CustomEvent('refresh-unread-messages'));
          }).catch(() => {});
          return [...prev, ...newOnes];
        });
      } catch {
        // silently ignore
      } finally {
        isPollingRef.current = false;
      }
    };

    pollingRef.current = setInterval(poll, POLL_INTERVAL);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [userId, loading]);

  // Auto-scroll when new messages arrive (only if near bottom or own message)
  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.id === lastMessageIdRef.current) return;
    lastMessageIdRef.current = lastMsg.id;

    // Skip auto-scroll on initial load (handled separately)
    if (!initialScrollDoneRef.current) return;

    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    // Auto-scroll if user is near bottom or if it's their own message
    if (distanceFromBottom < 150 || lastMsg.senderId === currentUserId) {
      scrollToBottom('smooth');
    }
  }, [messages, currentUserId, scrollToBottom]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await communityService.getMessages(userId!, page);
      // Backend already returns messages in chronological order (oldest first)
      // Do NOT reverse them!
      if (page === 1) {
        setMessages(data.messages);
        setOtherUser(data.otherUser);
      } else {
        // Older messages go to the beginning
        setMessages(prev => [...data.messages, ...prev]);
      }
      setHasMore(data.pagination.hasMore);
    } catch (err: any) {
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  };

  // Scroll to bottom after initial messages load
  useEffect(() => {
    if (!loading && !initialScrollDoneRef.current && messages.length > 0) {
      initialScrollDoneRef.current = true;
      // Use multiple rAF to ensure DOM is fully painted before scrolling
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          forceScrollToBottom();
        });
      });
    }
  }, [loading, messages.length, forceScrollToBottom]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || sending) return;

    // Optimistic UI: add message immediately
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg: Message = {
      id: tempId,
      content: text,
      senderId: currentUserId!,
      receiverId: userId!,
      isRead: false,
      createdAt: new Date().toISOString(),
      sender: { firstName: 'Tu', lastName: '', profilePhotoUrl: null },
    } as any;

    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage('');
    // Scroll to bottom after sending
    requestAnimationFrame(() => {
      forceScrollToBottom();
    });
    inputRef.current?.focus();

    try {
      setSending(true);
      const message = await communityService.sendMessage(userId!, text);
      // Replace optimistic message with real one
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempId);
        const existingIds = new Set(withoutTemp.map(m => m.id));
        if (existingIds.has(message.id)) return withoutTemp;
        return [...withoutTemp, message];
      });
    } catch (err: any) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(text); // restore text
      alert(err.response?.data?.error || 'Eroare la trimiterea mesajului');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as any);
    }
  };

  const formatTime = (date: string) => {
    try {
      const d = new Date(date);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'acum';
      if (diffMins < 60) return `${diffMins}m`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h`;
      return formatDistanceToNow(d, { addSuffix: false, locale: ro });
    } catch {
      return '';
    }
  };

  const formatLastSeen = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ro });
    } catch {
      return 'recent';
    }
  };

  // Group messages by date (messages are already in chronological order)
  const groupedMessages = messages.reduce((groups: { date: string; msgs: Message[] }[], msg) => {
    const date = new Date(msg.createdAt).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
    const last = groups[groups.length - 1];
    if (last && last.date === date) {
      last.msgs.push(msg);
    } else {
      groups.push({ date, msgs: [msg] });
    }
    return groups;
  }, []);

  return (
    <div className="flex flex-col bg-gray-100" style={{ height: '100dvh' }}>
      {/* Header - fixed */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 shadow-sm"
           style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
        <button
          onClick={() => navigate('/community/messages')}
          className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {otherUser ? (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {otherUser.profilePhotoUrl ? (
                <img src={otherUser.profilePhotoUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                `${otherUser.firstName?.[0] || ''}${otherUser.lastName?.[0] || ''}`.toUpperCase()
              )}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">
                {otherUser.firstName} {otherUser.lastName}
              </p>
              <p className={`text-xs font-medium ${isOnline ? 'text-green-500' : 'text-gray-400'}`}>
                {isOnline ? 'online' : lastSeen ? `activ ${formatLastSeen(lastSeen)}` : 'offline'}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
          </div>
        )}
      </div>

      {/* Messages area - scrollable */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-1"
        style={{ overscrollBehavior: 'contain' }}
      >
        {/* Load more */}
        {hasMore && (
          <div className="text-center mb-4">
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200"
            >
              {loading ? 'Se încarcă...' : '↑ Mesaje mai vechi'}
            </button>
          </div>
        )}

        {loading && page === 1 ? (
          <div className="flex items-center justify-center h-full py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="text-5xl mb-3">💬</div>
            <p className="text-gray-500 text-sm">Nicio conversație încă.<br />Trimite primul mesaj!</p>
          </div>
        ) : (
          groupedMessages.map(({ date, msgs }) => (
            <div key={date}>
              {/* Date separator */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gray-300" />
                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">{date}</span>
                <div className="flex-1 h-px bg-gray-300" />
              </div>

              {msgs.map((message, idx) => {
                const isMe = message.senderId === currentUserId;
                const isTemp = message.id.startsWith('temp-');
                const prevMsg = idx > 0 ? msgs[idx - 1] : null;
                const showAvatar = !isMe && (!prevMsg || prevMsg.senderId !== message.senderId);

                return (
                  <div
                    key={message.id}
                    className={`flex mb-1 ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    {/* Avatar for other user */}
                    {!isMe && (
                      <div className="w-8 flex-shrink-0 mr-2 self-end">
                        {showAvatar && (
                          <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {message.sender?.firstName?.[0]?.toUpperCase() || 'U'}
                          </div>
                        )}
                      </div>
                    )}

                    <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div
                        className={`px-4 py-2 rounded-2xl text-sm break-words ${
                          isMe
                            ? `bg-blue-600 text-white rounded-br-sm ${isTemp ? 'opacity-70' : ''}`
                            : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                      <div className={`flex items-center gap-1 mt-0.5 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                        <span className="text-xs text-gray-400">{formatTime(message.createdAt)}</span>
                        {isMe && !isTemp && (
                          <span className={`text-xs ${message.isRead ? 'text-blue-500' : 'text-gray-400'}`}>
                            {message.isRead ? '✓✓' : '✓'}
                          </span>
                        )}
                        {isTemp && <span className="text-xs text-gray-400">⏳</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        <div ref={messagesEndRef} className="h-1" />
      </div>

      {/* Scroll to bottom button */}
      {showScrollDown && (
        <button
          onClick={() => scrollToBottom('smooth')}
          className="absolute bottom-24 right-4 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors z-10"
        >
          <ChevronDown className="w-5 h-5" />
        </button>
      )}

      {/* Input area - fixed at bottom */}
      <div
        className="bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scrie un mesaj..."
            className="flex-1 bg-gray-100 border-0 rounded-full px-4 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            style={{ fontSize: '16px' }}
            disabled={sending}
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
